/**
 * StatsWindow Component
 *
 * Pre-configured window for stats/metrics visualization.
 * Uses chart components to display career data.
 */

import { useState, useMemo } from 'react'
import { MacStudioWindow } from '../MacStudioWindow'
import { FinderToolbar } from './FinderToolbar'
import { FinderSplitPane } from './FinderSplitPane'
import { FinderSidebar } from './FinderSidebar'
import { TimelineChart } from '../charts/TimelineChart'
import { RangeBarsChart } from '../charts/RangeBarsChart'
import { CareerGanttChart } from '../charts/CareerGanttChart'
import { MetricsCard } from '../charts/MetricsCard'
import { stats } from '@/generated/content'
import type { CategoryConfig } from './FinderWindow'
import './finder.css'

interface StatsWindowProps {
  windowId: string
  initialPosition?: { x: number; y: number }
}

// Default icons for stat types
const statIcons: Record<string, string> = {
  locations: '📍',
  experience: '📊',
  companies: '🏢',
  teams: '👥',
}

export function StatsWindow({
  windowId,
  initialPosition,
}: StatsWindowProps) {
  // Build categories from stats data
  const categories: CategoryConfig[] = useMemo(() => {
    return stats.map((stat) => ({
      id: stat.slug,
      name: stat.title.replace('Where I\'ve Lived & Worked', 'Locations').split(' ')[0],
      icon: statIcons[stat.slug] || '📈',
      description: stat.title,
    }))
  }, [])

  const [selectedCategory, setSelectedCategory] = useState<string>(
    categories[0]?.id || 'locations'
  )

  // Get current stat data
  const currentStat = useMemo(() => {
    return stats.find((s) => s.slug === selectedCategory)
  }, [selectedCategory])

  // Render the appropriate chart based on stat type
  const renderChart = () => {
    if (!currentStat) {
      return (
        <div className="stats-window__empty">
          <p>Select a stat category to view</p>
        </div>
      )
    }

    const { type, entries, title } = currentStat

    switch (type) {
      case 'timeline':
        return (
          <TimelineChart
            entries={entries as Parameters<typeof TimelineChart>[0]['entries']}
            startYear={currentStat.startYear || 1970}
            endYear={currentStat.endYear || 2026}
            title={title}
          />
        )

      case 'range-bars':
        return (
          <RangeBarsChart
            entries={entries as Parameters<typeof RangeBarsChart>[0]['entries']}
            startYear={currentStat.startYear || 1990}
            endYear={currentStat.endYear || 2026}
            title={title}
          />
        )

      case 'gantt':
        return (
          <CareerGanttChart
            entries={entries as Parameters<typeof CareerGanttChart>[0]['entries']}
            startYear={currentStat.startYear || 1994}
            endYear={currentStat.endYear || 2026}
            title={title}
          />
        )

      case 'metrics':
        return (
          <MetricsCard
            entries={entries as Parameters<typeof MetricsCard>[0]['entries']}
            title={title}
          />
        )

      default:
        return (
          <div className="stats-window__empty">
            <p>Unknown stat type: {type}</p>
          </div>
        )
    }
  }

  const handleCategorySelect = (categoryId: string) => {
    setSelectedCategory(categoryId)
  }

  const breadcrumb = [
    { id: 'root', name: 'Stats' },
    { id: selectedCategory, name: currentStat?.title || selectedCategory },
  ]

  return (
    <MacStudioWindow
      windowId={windowId}
      title="Stats"
      size="large"
      initialPosition={initialPosition}
      className="finder-window stats-window"
    >
      <div className="finder-container">
        <FinderToolbar
          viewMode="icon"
          onViewModeChange={() => {}}
          canGoBack={false}
          canGoForward={false}
          onNavigateBack={() => {}}
          onNavigateForward={() => {}}
          breadcrumb={breadcrumb}
        />

        <FinderSplitPane
          sidebar={
            <FinderSidebar
              categories={categories}
              selectedCategory={selectedCategory}
              onCategorySelect={handleCategorySelect}
              title="Stats"
            />
          }
          content={
            <div className="stats-window__content">
              {renderChart()}
            </div>
          }
          storageKey="stats-window"
        />
      </div>
    </MacStudioWindow>
  )
}

export default StatsWindow
